import { useContext, useEffect, useState } from 'react';
import axios from '../config/axios';
import { useAuth } from 'react-oidc-context';
import Loading from '../components/Loading';
import { UserContext } from '../context/userContext';
import { CartContext } from '../context/cartContext';
import {
  CartDetailType,
  Product,
  ProductListProps,
} from '../interface/interface';
import { calculateOutOfStockEnum } from '../utils/outOfStockStatus';
import showToast from '../components/ToastComponent';
import { useQuery } from '@tanstack/react-query';
import { getNonOrderableProducts } from '../service/getNonOrderableProducts';
import { PatientOrderTab } from './patientOrderTab';

export const ProductList = ({ home }: ProductListProps) => {
  const { user } = useAuth();
  const { cart, setCart } = useContext(CartContext);
  const { userDetail, setLoadingEnable } = useContext(UserContext);
  const [loading, setLoading] = useState(true);

  const [products, setProducts] = useState<Array<any>>([]);
  const [accessories, setAccessories] = useState<Array<any>>([]);

  const callCart = async () => {
    try {
      const token = user?.access_token;

      const response = await axios('/api/v1/cart', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const responseData = response?.data;
      setCart(responseData);
      await callProducts(responseData);
    } catch (error: any) {
      showToast({
        message: 'Unable to fetch existing cart!',
        type: 'error',
      });
      return;
    }
  };

  const frameProductObject = (
    products: Array<Product>,
    cart?: CartDetailType,
  ) => {
    const quantityCheckedProducts = products.map((product: any) => {
      const orderedQuantity =
        cart?.line_items?.find(
          (item: any) => `${item?.product_id}` === `${product.product_code}`,
        )?.quantity ?? 0;
      if (product.substituteProduct) {
        const subProduct = product.substituteProduct;
        const subOrderedQuantity =
          cart?.line_items?.find(
            (item: any) =>
              `${item?.product_id}` === `${subProduct.product_code}`,
          )?.quantity ?? 0;
        return {
          ...product,
          substituteProduct: {
            ...subProduct,
            productMaxQty: product.productMaxQty,
            orderedQuantity: subOrderedQuantity,
            outOfStock: calculateOutOfStockEnum(subProduct, orderedQuantity),
          },
          orderedQuantity,
          outOfStock: calculateOutOfStockEnum(product, orderedQuantity),
        };
      }

      return {
        ...product,
        orderedQuantity,
        outOfStock: calculateOutOfStockEnum(product, orderedQuantity),
      };
    });
    return quantityCheckedProducts;
  };

  const callProducts = async (cart?: CartDetailType) => {
    try {
      const response = await axios('/api/v1/products', {
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
        },
      });
      const products = response?.data;

      const quantityCheckedProducts = frameProductObject(products, cart);

      setProducts(quantityCheckedProducts);
      return true;
    } catch (error) {
      return false;
    }
  };

  const callTestKitAccessories = async (cart?: CartDetailType) => {
    try {
      const response = await axios('/api/v1/testkit/accessories', {
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
        },
      });
      const products = response?.data;

      const quantityCheckedProducts = frameProductObject(products, cart);

      setAccessories(quantityCheckedProducts);
      return true;
    } catch (error) {
      return false;
    }
  };

  async function addProductToCart(id: string, substituteForId?: string) {
    try {
      const data = {
        product_id: id,
        substituteForId,
      };

      const res = await axios.post('/api/v1/cart/add', data, {
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
        },
        validateStatus: (status) => {
          return status < 500;
        },
      });

      if (res.status === 200) {
        showToast({
          message: 'Item added to your cart',
          type: 'success',
        });

        await callCart();
      } else {
        let message = res.data.message;

        if (message === 'Email is invalid')
          message = `You cannot place orders as your email is invalid.
        Please update your email address in your profile. Note, this will also change your login username.`;

        showToast({
          message: message,
          type: 'warning',
          timeout: 6000,
        });
      }
    } catch (error: any) {
      //Do Nothing
      console.error(error);
      showToast({
        message: 'Failed to add item to the cart',
        type: 'error',
      });
    }
  }

  async function removeProductFromCart(id: string) {
    try {
      const data = {
        product_id: id,
      };

      const res = await axios.post('/api/v1/cart/remove', data, {
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
        },
        validateStatus: (status) => {
          return status < 505;
        },
      });

      if (res.status === 200) {
        showToast({
          message: 'Item removed from your cart',
          type: 'success',
        });
        await callCart();
      } else {
        showToast({
          message: res.data.message,
          type: 'warning',
        });
      }
    } catch (error: any) {
      console.error(error);
      showToast({
        message: 'Failed to remove item from the cart',
        type: 'error',
      });
    }
  }

useEffect(() => {
  const fetchData = async () => {
    setLoading(true);
    await callProducts(cart);
    await callTestKitAccessories(cart);
    setLoading(false);
  };

  fetchData();
}, [cart]);

  useEffect(() => {
    if (!loading) {
      setLoadingEnable(false);
    } else {
      setLoadingEnable(true);
    }
  }, [loading]);

  const { isLoading, data: nonOrderable } = useQuery({
    queryKey: ['non-orderable-products', user?.access_token],
    queryFn: ({ queryKey }) => getNonOrderableProducts(queryKey[1]!),
  });

  if (loading || isLoading) {
    return <Loading />;
  }

  if (!products?.length && !nonOrderable?.length) {
    return <p className='mt-5'>No active Prescription Exist </p>;
  }

  return (
    <div>
      <p className="lead mb-5 hidden">
        {`The list of products for user, ${userDetail.name}, based on your prescriptions and Storefront (Shopify) products.`}
      </p>
      {!loading ? (
        <PatientOrderTab
          products={[...products, ...accessories]}
          addProductToCart={addProductToCart}
          removeProductFromCart={removeProductFromCart}
          nonOrderable={nonOrderable}
          isHome={home}
        />
      ) : null}
    </div>
  );
};

export default ProductList;
